// src/core/registration/register_data.ts
import { useState } from "react";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import {
  arrowTableToObjects,
  dropRelation,
  registerFileBuffer,
  registerCsv,
  registerParquet,
  registerJson,
  registerTableFromUrl,
} from "@/lib";
import { classifyRegistrationError } from "./classifyRegistrationError";

export interface RegistrationResult {
  success: boolean;
  tableName: string;
  rowCount: number;
  columnCount: number;
  error?: string;
  errorSource?: "file" | "url" | "format" | "memory" | "database" | "unknown";
}

/**
 * Hook: handle table registration from three possible sources
 * (remote URL → pasted JSON → local file).
 */
export function useTableRegistration(
  conn: AsyncDuckDBConnection | null,
  setMsg?: (msg: string) => void
) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RegistrationResult | null>(null);
  const [sampleData, setSampleData] = useState<any[]>([]);
  const [sampleCols, setSampleCols] = useState<string[]>([]);

  async function registerTable(
    tableName: string,
    file: File | null,
    url: string,
    inlineJson: string
  ) {
    if (!conn) return;

    /* input validation -------------------------------------------------- */
    if (!url.trim() && !inlineJson.trim() && !file) {
      setMsg?.("Please provide a remote URL, paste JSON, or select a local file.");
      return;
    }
    if (!tableName.trim()) {
      setMsg?.("Please provide a table name.");
      return;
    }

    try {
      setLoading(true);
      setResult(null);
      setMsg?.("Registering table…");

      /* 1️⃣ Remote URL (takes precedence) ------------------------------ */
      if (url.trim()) {
        await registerTableFromUrl(conn, tableName, url.trim());
      }
      /* 2️⃣ Inline JSON ------------------------------------------------ */
      else if (inlineJson.trim()) {
        /* quick client-side validation for clearer error messaging */
        try {
          JSON.parse(inlineJson);
        } catch (e) {
          throw new Error("Invalid JSON: " + (e as Error).message);
        }

        const filename = `${tableName || "inline"}.json`;
        const buf = new TextEncoder().encode(inlineJson);
        await registerFileBuffer(conn, filename, buf);
        await dropRelation(conn, tableName);
        await registerJson(conn, tableName, filename);
      }
      /* 3️⃣ Local file -------------------------------------------------- */
      else {
        const filename = file!.name;
        const buf = new Uint8Array(await file!.arrayBuffer());
        await registerFileBuffer(conn, filename, buf);
        await dropRelation(conn, tableName);

        const ext = filename.split(".").pop()!.toLowerCase();
        if (ext === "csv") {
          await registerCsv(conn, tableName, filename);
        } else if (ext === "parquet") {
          await registerParquet(conn, tableName, filename);
        } else if (ext === "json") {
          await registerJson(conn, tableName, filename);
        } else {
          throw new Error(
            "Unsupported file type. Only CSV, Parquet or JSON are supported."
          );
        }
      }

      /* ── stats + sample preview ------------------------------------- */
      const sampleRows = arrowTableToObjects(
        await conn.query(`SELECT * FROM "${tableName}" LIMIT 5`)
      );
      const cols = sampleRows.length ? Object.keys(sampleRows[0]) : [];
      setSampleData(sampleRows);
      setSampleCols(cols);

      const countRes = await conn.query(
        `SELECT COUNT(*) AS c FROM "${tableName}"`
      );
      const rowCount = Number(arrowTableToObjects(countRes)[0]?.c ?? 0);

      setResult({
        success: true,
        tableName,
        rowCount,
        columnCount: cols.length,
      });
      setMsg?.(
        `Table "${tableName}" registered successfully with ${rowCount.toLocaleString()} rows and ${cols.length} columns.`
      );
    } catch (err) {
      const error = err as Error;
      setResult({
        success: false,
        tableName,
        rowCount: 0,
        columnCount: 0,
        error: error.message,
        errorSource: classifyRegistrationError(error.message),
      });
      setMsg?.(`Error registering table: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  return { registerTable, loading, result, sampleData, sampleCols };
}
